import type { DeckStudyCard } from "@-/dev.improve.haniverse.lib.cards";
import type {
  MorphemeDetailsTileSlice,
  MorphemeDetailsTileSliceGraphemeMorphemeSlice,
  MorphemeDetailsTileSliceWordMorphemeSlice,
} from "@-/dev.improve.haniverse.lib.morpheme.entities";

/**
 * A PROVISIONAL `MorphemeDetailsTileSlice` per deck front, built from the actor rows alone.
 *
 * This is what the deck paints with on its first render, before (and without waiting for) the
 * content/static-DB details. The actor overlays carry only card IDENTITY, which is enough for the
 * glyph tile to be recognisable and correctly ordered: the front itself (the glyph and its tone
 * color come from the bundled dictionary), a pronunciation card's pinyin and a word card's
 * pinyin. Everything content owns — glosses, definitions, authoring completion, frequency ranks,
 * HSK/TBCL levels — is left unset and arrives when the real slice replaces this one (see
 * `useDeckSectionCards`); tiles show "…" for the missing answer text in the meantime.
 *
 * Feed it every card of the deck (a grapheme's pronunciation cards fill the meaning tile's
 * `pinyinList`), not just the filtered list.
 */
export function buildDeckCoreSliceByFront(
  studyCards: readonly DeckStudyCard[],
): ReadonlyMap<string, MorphemeDetailsTileSlice> {
  const graphemeSliceByFront = new Map<string, MorphemeDetailsTileSliceGraphemeMorphemeSlice>();
  const wordSliceByFront = new Map<string, MorphemeDetailsTileSliceWordMorphemeSlice>();

  for (const studyCard of studyCards) {
    const meaningCard = studyCard.userGraphemeMeaningCard;
    const pronunciationCard = studyCard.userGraphemePronunciationCard;
    const wordCard = studyCard.userWordSentenceCard;

    const grapheme = meaningCard?.grapheme ?? pronunciationCard?.grapheme;
    if (grapheme !== undefined) {
      const slice = graphemeSliceByFront.get(grapheme) ?? {
        front: grapheme,
        morphemeType: "grapheme",
        pinyinList: [],
        tier: null,
      };
      if (
        pronunciationCard != null &&
        !slice.pinyinList.some((entry) => entry.toneNumbersPinyin === pronunciationCard.pinyin)
      ) {
        slice.pinyinList.push({ toneNumbersPinyin: pronunciationCard.pinyin });
      }
      graphemeSliceByFront.set(grapheme, slice);
      continue;
    }

    if (wordCard != null && !wordSliceByFront.has(wordCard.word)) {
      wordSliceByFront.set(wordCard.word, {
        front: wordCard.word,
        morphemeType: "word",
        pinyinList: [{ toneNumbersPinyin: wordCard.pinyin }],
        tier: null,
      });
    }
  }

  return new Map<string, MorphemeDetailsTileSlice>([
    ...graphemeSliceByFront,
    ...wordSliceByFront,
  ]);
}

/**
 * Index source rows once, then construct only a revealed front's provisional slice. All of a
 * front's rows belong here, including pronunciation cards hidden by the deck's tile filters.
 * The sections own returned slices; this lookup never accumulates a second cache of them.
 */
export function createDeckCoreSliceLookup(
  studyCards: readonly DeckStudyCard[],
): Pick<ReadonlyMap<string, MorphemeDetailsTileSlice>, "get"> {
  const rowsByFront = new Map<string, DeckStudyCard[]>();
  for (const studyCard of studyCards) {
    const front =
      studyCard.userGraphemeMeaningCard?.grapheme ??
      studyCard.userGraphemePronunciationCard?.grapheme ??
      studyCard.userWordSentenceCard?.word;
    if (front === undefined) {
      continue;
    }
    const rows = rowsByFront.get(front);
    if (rows === undefined) {
      rowsByFront.set(front, [studyCard]);
    } else {
      rows.push(studyCard);
    }
  }
  return {
    get(front) {
      const rows = rowsByFront.get(front);
      return rows === undefined ? undefined : buildDeckCoreSliceByFront(rows).get(front);
    },
  };
}
